import { Navigate, useLocation, useNavigate } from 'react-router';
import { useAppTFunction } from '@retail/app/i18n';
import {
  GrayStyledAutocomplete,
  NewFunctionalityChip,
  PageLayout,
  ToggleSwitch,
} from '@retail/components';
import { useAvailableCalculusAreas } from '@retail/calculus/context';
import { CalculusTab } from '@retail/calculus/types';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { CalculusTabs, CombinedViewIntroDialog } from '@retail/calculus/components';
import { useCalculusCombinedView, useSelectLocalStorageSupplier } from '@retail/calculus/hooks';
import { Paper } from '@shared/components';
import { Stack } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useFetchAllSuppliers } from '@retail/products/data-access';
import { CalculusContainer } from '@retail/calculus/containers';
import { getCalculusAreaFromTab } from '@retail/calculus/utils';
import { AssortmentItemsCalculusView } from '@retail/my-assortment/containers';
import { SupplierBase } from '@retail/suppliers/types';
import { useFilters } from '@retail/products/hooks';

const calculusRoute = mgPrisRoutes.calculus.calculusArea;

const separatedConditionTabs = [
  CalculusTab.PURCHASE,
  CalculusTab.FREIGHT,
  CalculusTab.MARKUP,
  CalculusTab.SALES_PRICES,
  CalculusTab.ROUNDING,
  CalculusTab.CUSTOMER_MARKUP,
];

export function CalculusPage() {
  const t = useAppTFunction();
  const navigate = useNavigate();
  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();

  const { data: supplierDtos = [], isLoading: isLoadingSuppliers } = useFetchAllSuppliers(
    { assortmentId: selectedOrgUnitAssortment.draftAssortmentId },
    { suspense: false }
  );
  const suppliers = useMemo(() => supplierDtos.map(SupplierBase.fromSupplierDTO), [supplierDtos]);
  const { lastSelectedSupplier, setSelectedSupplier } = useSelectLocalStorageSupplier();

  const { filters, setFilters } = useFilters(['supplierNumber']);
  const selectedSupplier = useMemo(
    () =>
      suppliers.find((supplier) => supplier.supplierNumber.toString() === filters.supplierNumber) ??
      suppliers.find((supplier) => supplier.supplierNumber.toString() === lastSelectedSupplier) ??
      null,
    [filters.supplierNumber, lastSelectedSupplier, suppliers]
  );
  const handleSelectSupplier = useCallback(
    (supplierNumber?: number) => {
      setFilters({
        supplierNumber: supplierNumber ? supplierNumber.toString() : undefined,
      });
      setSelectedSupplier(supplierNumber);
    },
    [setFilters, setSelectedSupplier]
  );

  const { pathname } = useLocation();
  const urlCalculusArea = pathname.split('/').filter(Boolean).pop() as CalculusTab;

  const availableCalculusAreas = useAvailableCalculusAreas();

  const { combinedView, setCombinedView, hasStoredPreference } = useCalculusCombinedView();
  const allConditionsAvailable = availableCalculusAreas.includes(CalculusTab.ALL_CONDITIONS);
  const combinedViewActive = allConditionsAvailable && combinedView;

  const allTabsOrdered = useMemo(
    () => [
      { label: t('markup.itemPrices'), value: CalculusTab.ITEMS },
      { label: t('app.routes.agreements'), value: CalculusTab.PURCHASE },
      { label: t('app.routes.freight'), value: CalculusTab.FREIGHT },
      { label: t('app.routes.markup'), value: CalculusTab.MARKUP },
      { label: t('app.routes.priceSuggestion'), value: CalculusTab.PRICE_SUGGESTION },
      { label: t('app.routes.customerMarkup'), value: CalculusTab.CUSTOMER_MARKUP },
      { label: t('app.routes.salesPriceCondition'), value: CalculusTab.SALES_PRICES },
      { label: t('app.routes.roundingConditions'), value: CalculusTab.ROUNDING },
      {
        label: (
          <Stack direction="row" gap={0.5}>
            {t('app.routes.allConditions')}
            <NewFunctionalityChip />
          </Stack>
        ),
        value: CalculusTab.ALL_CONDITIONS,
      },
    ],
    [t]
  );

  const visibleTabs = useMemo(
    () =>
      allTabsOrdered.filter((tab) => {
        if (!availableCalculusAreas.includes(tab.value)) return false;
        return combinedViewActive
          ? !separatedConditionTabs.includes(tab.value)
          : tab.value !== CalculusTab.ALL_CONDITIONS;
      }),
    [allTabsOrdered, availableCalculusAreas, combinedViewActive]
  );

  const calculusArea = visibleTabs.find((tab) => tab.value === urlCalculusArea)?.value;

  const [, startTransition] = useTransition();
  const [selectedTab, setSelectedTab] = useState(calculusArea);

  useEffect(() => {
    if (calculusArea) setSelectedTab(calculusArea);
  }, [calculusArea]);

  const navigateToArea = useCallback(
    (area: CalculusTab) => {
      setSelectedTab(area);
      startTransition(() => {
        navigate({
          pathname: calculusRoute.getFullLinkPath({ calculusArea: area }),
          search: selectedSupplier ? `supplierNumber=${selectedSupplier.supplierNumber}` : '',
        });
      });
    },
    [navigate, selectedSupplier]
  );

  const applyCombinedView = useCallback(
    (enabled: boolean, targetArea: CalculusTab) => {
      setCombinedView(enabled);
      navigateToArea(targetArea);
    },
    [setCombinedView, navigateToArea]
  );

  const [introModalOpen, setIntroModalOpen] = useState(false);

  useEffect(() => {
    if (allConditionsAvailable && !hasStoredPreference) {
      setIntroModalOpen(true);
    }
  }, [allConditionsAvailable, hasStoredPreference]);

  const handleIntroResponse = useCallback(
    (accepted: boolean) => {
      setIntroModalOpen(false);
      applyCombinedView(accepted, accepted ? CalculusTab.ALL_CONDITIONS : CalculusTab.ITEMS);
    },
    [applyCombinedView]
  );

  if (!calculusArea)
    return (
      <Navigate to={calculusRoute.getFullLinkPath({ calculusArea: CalculusTab.ITEMS })} replace />
    );
  return (
    <PageLayout maxWidth="full" fixedHeight spacing={2}>
      <Stack height="100%" overflow="hidden">
        <Stack flexDirection="row" justifyContent="space-between" alignItems="flex-end">
          <CalculusTabs
            tabs={visibleTabs}
            currentCalculusArea={selectedTab ?? calculusArea}
            setCalculusArea={navigateToArea}
          />
          <Stack flexDirection="row" alignItems="center" gap={2}>
            {allConditionsAvailable && (
              <ToggleSwitch
                toggle={combinedViewActive}
                setToggle={(enabled) => applyCombinedView(enabled, CalculusTab.ITEMS)}
                text={t('calculus.combinedView.label')}
              />
            )}

            <GrayStyledAutocomplete
              options={suppliers}
              isLoading={isLoadingSuppliers}
              nonListOption={selectedOrgUnitAssortment.name}
              searchFieldPlaceholder={t('calculus.searchForSuppliers')}
              selectedOption={selectedSupplier ?? undefined}
              getOptionLabel={(supplier?: SupplierBase) =>
                supplier?.name ?? selectedOrgUnitAssortment.name
              }
              getOptionId={(supplier: SupplierBase) => supplier.supplierNumber}
              onSelectOption={handleSelectSupplier}
            />
          </Stack>
        </Stack>

        {allConditionsAvailable && (
          <CombinedViewIntroDialog
            open={introModalOpen}
            onAccept={() => handleIntroResponse(true)}
            onDecline={() => handleIntroResponse(false)}
          />
        )}

        <Paper sx={{ display: 'flex', height: '100%', overflow: 'auto', p: 0 }}>
          <Stack flexGrow={1} overflow="hidden">
            {calculusArea === CalculusTab.ITEMS && (
              <AssortmentItemsCalculusView
                supplierNumber={selectedSupplier?.supplierNumber}
                isLoadingSuppliers={isLoadingSuppliers}
              />
            )}

            {calculusArea !== CalculusTab.ITEMS && (
              <CalculusContainer
                calculusArea={getCalculusAreaFromTab(calculusArea)}
                selectedSupplier={selectedSupplier ?? undefined}
                isLoadingSuppliers={isLoadingSuppliers}
              />
            )}
          </Stack>
        </Paper>
      </Stack>
    </PageLayout>
  );
}
